import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

import type { KanbanStage } from '@/components/kanban/KanbanBoard'

// ============================================================
// TYPES
// ============================================================

export interface FunnelItem {
  id: string
  name: string
  isDefault: boolean
  sortOrder: number
}

export interface CrmDealCard {
  id: string
  name: string
  number: number
  contactName: string | null
  responsibleName: string
  responsibleUserId: string
  priority: number
  budget: number | null
  stageId: string
  createdAt: string
  lastComment: string | null
}

interface CrmFilters {
  search: string
  stageId: string
  responsibleUserId: string
  dateFrom: string
  dateTo: string
}

// quick-260527-wgn (Bug 3 UI) — extended stage descriptor used by the toolbar's
// stage-filter chip. Carries isHidden/isFinal so the filter menu can show
// closed-won/lost/cancelled stages, while `stages` (KanbanStage[]) keeps only
// visible columns for the kanban board.
export interface CrmStageOption extends KanbanStage {
  isHidden: boolean
  isFinal: boolean
}

interface CrmKanbanState {
  funnels: FunnelItem[]
  selectedFunnelId: string | null
  stages: KanbanStage[]
  // quick-260527-wgn (Bug 3 UI) — raw stages including isHidden/isFinal,
  // used ONLY by CrmBoardToolbar stage-filter chip.
  allStages: CrmStageOption[]
  deals: Record<string, CrmDealCard[]>
  loading: boolean
  filters: CrmFilters
  snapshot: Record<string, CrmDealCard[]> | null
}

const initialFilters: CrmFilters = {
  search: '',
  stageId: '',
  responsibleUserId: '',
  dateFrom: '',
  dateTo: ''
}

const initialState: CrmKanbanState = {
  funnels: [],
  selectedFunnelId: null,
  stages: [],
  allStages: [],
  deals: {},
  loading: false,
  filters: initialFilters,
  snapshot: null
}

// ============================================================
// HELPERS
// ============================================================

function buildQueryParams(filters: CrmFilters): URLSearchParams {
  const params = new URLSearchParams()

  // Fetch all deals for Kanban (high limit)
  params.set('limit', '200')
  params.set('page', '1')

  if (filters.search) params.set('search', filters.search)
  if (filters.stageId) params.set('stageId', filters.stageId)
  if (filters.responsibleUserId) params.set('responsibleUserId', filters.responsibleUserId)
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom)
  if (filters.dateTo) params.set('dateTo', filters.dateTo)

  return params
}

function groupDealsByStage(
  deals: CrmDealCard[],
  stages: KanbanStage[]
): Record<string, CrmDealCard[]> {
  const grouped: Record<string, CrmDealCard[]> = {}

  for (const stage of stages) {
    grouped[stage.id] = []
  }

  for (const deal of deals) {
    if (grouped[deal.stageId]) {
      grouped[deal.stageId].push(deal)
    }
  }

  return grouped
}

function mapApiDealToCard(deal: Record<string, unknown>): CrmDealCard {
  const contact = deal.contact as { name: string } | null
  const responsible = deal.responsibleUser as { id: string; name: string } | null

  return {
    id: deal.id as string,
    name: deal.name as string,
    number: deal.number as number,
    contactName: contact?.name ?? null,
    responsibleName: responsible?.name ?? '',
    responsibleUserId: responsible?.id ?? (deal.responsibleUserId as string),
    priority: deal.priority as number,
    budget: deal.budget as number | null,
    stageId: deal.stageId as string,
    createdAt: deal.createdAt as string,
    lastComment: null
  }
}

// ============================================================
// THUNKS
// ============================================================

export const fetchFunnels = createAsyncThunk(
  'crmKanban/fetchFunnels',
  async () => {
    const res = await fetch('/api/crm/funnels')
    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to fetch funnels')
    }

    return json.data as FunnelItem[]
  }
)

export const fetchCrmStages = createAsyncThunk(
  'crmKanban/fetchStages',
  async (_, { getState }) => {
    const state = getState() as { crmKanbanReducer: CrmKanbanState }
    const { selectedFunnelId } = state.crmKanbanReducer
    const params = selectedFunnelId ? `?funnelId=${selectedFunnelId}` : ''
    // quick-260527-wgn — auth-only mirror; `/api/settings/crm-stages` is ADMIN-only
    // and would 403 for EMPLOYEE → empty kanban columns (Bug 1).
    const res = await fetch(`/api/crm-stages${params}`)
    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to fetch CRM stages')
    }

    return json.data as KanbanStage[]
  }
)

export const fetchCrmDeals = createAsyncThunk(
  'crmKanban/fetchDeals',
  async (_, { getState }) => {
    const state = getState() as { crmKanbanReducer: CrmKanbanState }
    const { filters, selectedFunnelId } = state.crmKanbanReducer
    const params = buildQueryParams(filters)

    if (selectedFunnelId) {
      params.set('funnelId', selectedFunnelId)
    }

    const res = await fetch(`/api/crm/deals?${params}`)
    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to fetch deals')
    }

    return (json.data as Record<string, unknown>[]).map(mapApiDealToCard)
  }
)

export const moveDealStage = createAsyncThunk(
  'crmKanban/moveStage',
  async ({ dealId, stageId }: { dealId: string; stageId: string }) => {
    const res = await fetch(`/api/crm/deals/${dealId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stageId })
    })

    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to move deal')
    }

    return { dealId, stageId }
  }
)

export const createDeal = createAsyncThunk(
  'crmKanban/createDeal',
  async (data: { name: string; contactName: string; funnelId?: string; [key: string]: unknown }, { getState }) => {
    const state = getState() as { crmKanbanReducer: CrmKanbanState }
    const { selectedFunnelId } = state.crmKanbanReducer
    const body = { ...data, funnelId: data.funnelId ?? selectedFunnelId }

    const res = await fetch('/api/crm/deals', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })

    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to create deal')
    }

    return mapApiDealToCard(json.data as Record<string, unknown>)
  }
)

// ============================================================
// SLICE
// ============================================================

export const crmKanbanSlice = createSlice({
  name: 'crmKanban',
  initialState,
  reducers: {
    setSelectedFunnel: (state, action: PayloadAction<string>) => {
      return { ...state, selectedFunnelId: action.payload }
    },
    setFilter: (state, action: PayloadAction<Partial<CrmFilters>>) => {
      return {
        ...state,
        filters: { ...state.filters, ...action.payload }
      }
    },
    clearFilters: (state) => {
      return {
        ...state,
        filters: initialFilters
      }
    }
  },
  extraReducers: (builder) => {
    // fetchFunnels
    builder.addCase(fetchFunnels.fulfilled, (state, action) => {
      const funnels = action.payload
      const defaultFunnel = funnels.find(f => f.isDefault)

      return {
        ...state,
        funnels,
        selectedFunnelId: state.selectedFunnelId ?? defaultFunnel?.id ?? funnels[0]?.id ?? null
      }
    })

    // fetchCrmStages — store BOTH:
    //   - allStages (raw with isHidden/isFinal) for CrmBoardToolbar stage-filter chip
    //     (quick-260527-wgn Bug 3 UI — фильтр должен показывать ВСЕ этапы воронки,
    //     включая closed-won/lost/cancelled)
    //   - stages (filtered for kanban-display — isHidden columns скрыты с доски)
    builder.addCase(fetchCrmStages.fulfilled, (state, action) => {
      const allStages: CrmStageOption[] = action.payload.map((s) => ({
        id: s.id,
        name: s.name,
        color: s.color,
        sortOrder: s.sortOrder,
        isHidden: (s as { isHidden?: boolean }).isHidden ?? false,
        isFinal: (s as { isFinal?: boolean }).isFinal ?? false
      }))

      const stages: KanbanStage[] = allStages
        .filter((s) => !s.isHidden)
        .map(({ id, name, color, sortOrder }) => ({ id, name, color, sortOrder }))

      return { ...state, allStages, stages }
    })

    // fetchCrmDeals
    builder.addCase(fetchCrmDeals.pending, (state) => {
      return { ...state, loading: true }
    })

    builder.addCase(fetchCrmDeals.fulfilled, (state, action) => {
      const deals = groupDealsByStage(action.payload, state.stages)

      return { ...state, deals, loading: false }
    })

    builder.addCase(fetchCrmDeals.rejected, (state) => {
      return { ...state, loading: false }
    })

    // moveDealStage -- optimistic update with snapshot for rollback
    builder.addCase(moveDealStage.pending, (state, action) => {
      const { dealId, stageId: toStageId } = action.meta.arg

      // Save snapshot for rollback
      const snapshot: Record<string, CrmDealCard[]> = {}

      for (const [key, cards] of Object.entries(state.deals)) {
        snapshot[key] = [...cards]
      }

      // Find and move deal optimistically
      let movedDeal: CrmDealCard | null = null
      const updatedDeals: Record<string, CrmDealCard[]> = {}

      for (const [sId, cards] of Object.entries(state.deals)) {
        const filtered = cards.filter(d => {
          if (d.id === dealId) {
            movedDeal = { ...d, stageId: toStageId }

            return false
          }

          return true
        })

        updatedDeals[sId] = filtered
      }

      if (movedDeal && updatedDeals[toStageId]) {
        updatedDeals[toStageId] = [...updatedDeals[toStageId], movedDeal]
      }

      return { ...state, deals: updatedDeals, snapshot }
    })

    builder.addCase(moveDealStage.fulfilled, (state) => {
      // Optimistic update already applied; clear snapshot
      return { ...state, snapshot: null }
    })

    builder.addCase(moveDealStage.rejected, (state) => {
      // Rollback to snapshot
      if (state.snapshot) {
        return { ...state, deals: state.snapshot, snapshot: null }
      }

      return state
    })

    // createDeal
    builder.addCase(createDeal.fulfilled, (state, action) => {
      const deal = action.payload
      const stageDeals = state.deals[deal.stageId] ?? []
      const updatedDeals = {
        ...state.deals,
        [deal.stageId]: [...stageDeals, deal]
      }

      return { ...state, deals: updatedDeals }
    })
  }
})

export const { setSelectedFunnel, setFilter, clearFilters } = crmKanbanSlice.actions

export default crmKanbanSlice.reducer
